import { useEffect, useRef } from 'react';

import { parseAdUnitTargetingParams } from 'common/utils/ad-unit-targeting';
import { clientLog } from 'utils/logger';

import { useGpt } from './use-gpt';

const log = clientLog.extend('gpt-adunit');

export function useGptSlot(
	id: string,
	path: string,
	sizes: googletag.GeneralSize,
	enabled: boolean,
	targeting?: string
) {
	const ready = useGpt({ enabled });
	const slotRef = useRef<googletag.Slot | null>(null);

	useEffect(() => {
		log('useGptSlot effect', { ready, enabled, id, path, hasSlot: !!slotRef.current });
		if (!ready) return undefined;

		if (!slotRef.current) {
			window.googletag.cmd.push(() => {
				slotRef.current = window.googletag.defineSlot(path, sizes, id);
				log('useGptSlot: defineSlot', { id, path, sizes, slotDefined: !!slotRef.current });
				if (!slotRef.current) return;

				slotRef.current.addService(window.googletag.pubads());

				const targets = parseAdUnitTargetingParams(targeting ?? '');

				if (Array.isArray(targets)) {
					targets.forEach(target => {
						const key = target.name;
						const value = target.value.split(',').map(item => item.trim());

						if (key && value) slotRef.current?.setTargeting(key, value);
					});
				}

				window.googletag.display(id);
				log('useGptSlot: display called', { id });
			});
		}

		return () => {
			if (slotRef.current) {
				log('useGptSlot: cleanup destroySlots', { id });
				window.googletag.cmd.push(() => {
					window.googletag.destroySlots([slotRef.current!]);
				});
			}
		};
	}, [ready, enabled, id, path, sizes, targeting]);

	return null;
}
